import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { FormControl, Select, MenuItem } from '@material-ui/core';
import { orderBy } from 'lodash';

import ArrowIcon from 'assets/img/dropdown-arrow.png';
import { LocationDTO } from '@peartherapeutics/weaver-api-client';

export interface IProps {
  disabled: boolean;
  options: LocationDTO[];
  placeholder?: string;
  onChangeLocation: (label: string, value: string) => void;
  hasSelectAll?: boolean;
  width?: string;
}

const useStyles = makeStyles(() => ({
  formControl: {
    width: '100%',
    border: '1px solid #ccc',
    '& .MuiInput-underline:before': {
      border: 'none',
    },
    '& .MuiInputBase-root': {
      color: '#5da0c5',
      opacity: 0.5,
    },
    '& .MuiInputBase-root:not(.Mui-disabled)': {
      opacity: 1,
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
      border: 'none',
    },
    '& .MuiInput-underline:after': {
      border: 'none',
    },
  },
  select: {
    height: '40px',
    color: '#5da0c5',
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '24px',
    background: 'white',
    '& .MuiSelect-root': {
      paddingLeft: '10px',
    },
    '& .MuiSelect-root:not(.Mui-disabled)': {
      background: 'transparent',
      '& div:first-child': {
        display: 'block',
      },
      '& div': {
        display: 'none',
      },
    },
  },
  selectIcon: {
    background: `white url(${ArrowIcon}) no-repeat`,
    backgroundSize: '100%',
    width: '14px',
    height: '9px',
    marginRight: '10px',
  },
  iconDown: {
    transform: 'rotate(180deg)',
  },
  selectList: {
    maxHeight: '290px',
    padding: '0',
    width: (props: any) => props.width || '345px',
    '& .Mui-selected, .Mui-selected:hover': {
      background: '#35ab8c',
      color: 'white',
    },
  },
  selectItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
    justifyContent: 'center',
    minHeight: '48px',
    color: '#4a4a4a',
    fontFamily: 'Source Sans Pro',
    '& div:first-child': {
      opacity: 1,
      fontSize: '14px',
      lineHeight: '24px',
    },
    '& div': {
      lineHeight: '18px',
      fontSize: '12px',
      opacity: 0.8,
    },
    '&:hover': {
      background: '#35ab8c',
      color: 'white',
    },
  },
  popover: {
    marginTop: '2px',
    borderRadius: '0px',
  },
}));

const SelectLocation = (props: IProps) => {
  const { placeholder, options, disabled, hasSelectAll, onChangeLocation } =
    props;
  const classes = useStyles(props);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [value, setValue] = React.useState('');
  const sortedOptions = orderBy(options, (option) =>
    option.name.toLowerCase().trim(),
  );

  const onChangeSelect = (e: any) => {
    const targetValue = JSON.parse(e.target.value);
    onChangeLocation(targetValue.label, targetValue.value);
    setValue(e.target.value);
  };

  const renderValue = () => {
    //* -----------------------------------------------
    // Display placeholder as default value
    //* -----------------------------------------------
    if (
      placeholder &&
      value === '' &&
      (options?.length !== 1 || (hasSelectAll && options?.length === 1))
    ) {
      // eslint-disable-next-line react/no-unstable-nested-components
      const Placeholder = () => <div>{placeholder}</div>;
      return Placeholder;
    }

    //* -----------------------------------------------
    // Auto select item if it's the only one present in the list
    //* -----------------------------------------------
    if (
      value === '' &&
      ((options?.length === 1 && !hasSelectAll) ||
        (hasSelectAll && options?.length === 0))
    ) {
      const location = options[0];
      // eslint-disable-next-line react/no-unstable-nested-components
      const AutoSelectedLocation = () => <div>{location.name.trim()}</div>;
      return AutoSelectedLocation;
    }

    //* -----------------------------------------------
    // Default selected value from Material-UI
    //* -----------------------------------------------
    return undefined;
  };

  const forceClearState = () => {
    if (
      (disabled && value !== '') ||
      (!disabled && options!.length === 0) ||
      (options && options!.length === 0)
    ) {
      setValue('');
    }

    if (!disabled && options!.length === 1 && !hasSelectAll) {
      const location = options![0];
      setValue(
        JSON.stringify({ label: location.name.trim(), value: location.id }),
      );
      onChangeLocation(location.name.trim(), location.id);
    }
  };

  React.useEffect(() => {
    forceClearState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, options, value]);

  return (
    <FormControl className={classes.formControl} disabled={disabled}>
      <Select
        data-testid="select-location"
        className={classes.select}
        displayEmpty
        value={options && options!.length === 0 ? '' : value}
        onChange={(e) => onChangeSelect(e)}
        onOpen={() => setIsExpanded(true)}
        onClose={() => setIsExpanded(false)}
        renderValue={renderValue()}
        MenuProps={{
          classes: { list: classes.selectList },
          transformOrigin: { vertical: 'top', horizontal: 'left' },
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          getContentAnchorEl: null,
          PopoverClasses: { paper: classes.popover },
        }}
        // eslint-disable-next-line react/no-unstable-nested-components
        IconComponent={(props) => (
          <i
            {...props}
            className={`${classes.selectIcon} ${
              isExpanded && classes.iconDown
            }`}
          />
        )}
      >
        {hasSelectAll && (
          <MenuItem
            data-testid="select-location-item"
            className={classes.selectItem}
            value={JSON.stringify({ label: 'All Clinics', value: '' })}
          >
            All Clinics
          </MenuItem>
        )}

        {sortedOptions.map((option) => {
          const { id, name, addressLine1, city, district, postalCode } = option;
          return (
            <MenuItem
              data-testid="select-location-item"
              className={classes.selectItem}
              key={id}
              value={JSON.stringify({ label: name.trim(), value: id })}
            >
              <div>{name.trim()}</div>
              <div>{addressLine1}</div>
              <div>
                {city}, {district} {postalCode}
              </div>
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

export default SelectLocation;
